import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { X, Minimize2, Maximize2, ChevronDown, Search, Check, ChevronsUpDown } from 'lucide-react';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import Select from 'react-select';
import {ChartFactory} from '../../../../ChartUtility/charts/index'
import { styles, customSelectStyles, getCustomSelectStyles, getPeriodsForFinancialYears, RepresentationTypeList ,AggreGatePeriodList} from "./helper";
import { sweetAlert } from "../../../../../utils/UniversalFunction";
import {getSubLocationsForLocations, getDomainCountForGroupField, AXIS_FIELD_LABEL_MAP , buildMeterMap, capitalizeWords} from './helper'
import { Loader2 } from "lucide-react";
import { TabularView } from "./components/TabularView";
import SummaryCard from './components/SummaryCard'

const AXIS_LABEL_TO_VALUE_MAP = Object.entries(AXIS_FIELD_LABEL_MAP)
  .reduce((acc, [key, label]) => {
    acc[label] = key;
    return acc;
  }, {});

const OPERATION_OPTIONS = [
    { value: 'sum', label: 'Sum' },
];

// ─── SORT FIELD DROPDOWN ──────────────────────────────────────────────────────
// A fully custom dropdown: search + select-all + checkboxes, no react-select dependency
const SortFieldDropdown = ({ options = [], value = [], onChange, disabled }) => {
    const [open, setOpen]       = useState(false);
    const [search, setSearch]   = useState('');
    const ref                   = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered      = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
    const allSelected   = filtered.length > 0 && filtered.every(o => value.some(v => v.value === o.value));
    const someSelected  = filtered.some(o => value.some(v => v.value === o.value)) && !allSelected;

    const toggle = (opt) => {
        const already = value.some(v => v.value === opt.value);
        onChange(already ? value.filter(v => v.value !== opt.value) : [...value, opt]);
    };

    const toggleAll = () => {
        if (allSelected) {
            onChange(value.filter(v => !filtered.some(f => f.value === v.value)));
        } else {
            const toAdd = filtered.filter(f => !value.some(v => v.value === f.value));
            onChange([...value, ...toAdd]);
        }
    };

    const displayLabel = value.length === 0
        ? (disabled ? 'Select fields in Chart Configuration first' : 'Select fields to sort by...')
        : value.length === options.length && options.length > 0
            ? 'All Fields'
            : value.map(v => v.label).join(', ');

    return (
        <div ref={ref} style={{ position: 'relative', width: '100%' }}>
            {/* Trigger */}
            <div
                onClick={() => !disabled && setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '7px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${open ? '#3f88a5' : '#d1d5db'}`,
                    background: disabled ? '#f9fafb' : 'white',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    color: value.length === 0 ? '#9ca3af' : '#1f2937',
                    boxShadow: open ? '0 0 0 3px rgba(63,136,165,0.12)' : 'none',
                    transition: 'all 0.15s ease',
                    minHeight: '36px',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontWeight: value.length > 0 ? 500 : 400,
                }}>
                    {displayLabel}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '6px' }}>
                    {value.length > 0 && (
                        <span style={{
                            fontSize: '11px', fontWeight: 700,
                            background: '#3f88a5', color: 'white',
                            borderRadius: '10px', padding: '1px 7px',
                            lineHeight: '16px',
                        }}>
                            {value.length}
                        </span>
                    )}
                   
                </div>
            </div>

            {/* Dropdown panel */}
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    zIndex: 9999,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}>
                    {/* Search */}
                    <div style={{
                        padding: '8px',
                        borderBottom: '1px solid #f3f4f6',
                        position: 'relative',
                    }}>
                        {/* <Search size={8} color="#9ca3af" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} /> */}
                        <input
                            autoFocus
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search fields..."
                            style={{
                                width: '100%', padding: '6px 8px 6px 28px',
                                border: '1px solid #e5e7eb', borderRadius: '5px',
                                fontSize: '12px', outline: 'none', color: '#374151',
                                background: '#f9fafb',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {/* Select All */}
                    {filtered.length > 1 && (
                        <div
                            onClick={toggleAll}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6',
                                background: '#fafafa',
                                fontSize: '12px', fontWeight: 600, color: '#374151',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
                        >
                            {/* Checkbox */}
                            <div style={{
                                width: 15, height: 15,
                                border: `1.5px solid ${allSelected || someSelected ? '#3f88a5' : '#c4c9d4'}`,
                                borderRadius: 3,
                                background: allSelected ? '#3f88a5' : someSelected ? '#3f88a5' : 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {allSelected && <Check size={9} color="white" strokeWidth={3} />}
                                {someSelected && !allSelected && (
                                    <div style={{ width: 7, height: 1.5, background: 'white', borderRadius: 1 }} />
                                )}
                            </div>
                            <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
                        </div>
                    )}

                    {/* Options */}
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
                                No fields found
                            </div>
                        ) : filtered.map(opt => {
                            const checked = value.some(v => v.value === opt.value);
                            return (
                                <div
                                    key={opt.value}
                                    onClick={() => toggle(opt)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '7px 12px',
                                        cursor: 'pointer',
                                        background: checked ? '#f0f9ff' : 'white',
                                        fontSize: '13px', color: '#374151',
                                    }}
                                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f3f8fb'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = checked ? '#f0f9ff' : 'white'; }}
                                >
                                    <div style={{
                                        width: 15, height: 15, flexShrink: 0,
                                        border: `1.5px solid ${checked ? '#3f88a5' : '#c4c9d4'}`,
                                        borderRadius: 3,
                                        background: checked ? '#3f88a5' : 'white',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {checked && <Check size={9} color="white" strokeWidth={3} />}
                                    </div>
                                    <span style={{ fontWeight: checked ? 500 : 400 }}>{opt.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── SELECT WITH ALL ──────────────────────────────────────────────────────────
const SelectWithAll = ({ options = [], value = [], onChange, placeholder, allLabel, styles: sStyles, isDisabled }) => {
    const allSelected = options.length > 0 && value.length === options.length;
    const someSelected = value.length > 0 && !allSelected;
    const displayValue = allSelected ? [{ value: '__ALL__', label: allLabel }] : value;
    const enhancedOptions = [
        { value: '__SELECT_ALL__', label: allSelected ? 'Deselect All' : 'Select All' },
        ...(options || [])
    ];

    const handleChange = (selected) => {
        if (!selected || selected.length === 0) { onChange([]); return; }
        if (allSelected && !selected.some(s => s.value === '__ALL__')) { onChange([]); return; }
        if (selected.some(s => s.value === '__SELECT_ALL__')) { onChange(allSelected ? [] : [...options]); return; }
        onChange(selected.filter(s => s.value !== '__ALL__' && s.value !== '__SELECT_ALL__'));
    };

    const formatOptionLabel = (option) => {
        const isSelectAll = option.value === '__SELECT_ALL__';
        const isChecked = isSelectAll ? allSelected : value?.some(v => v.value === option.value);
        const isIndeterminate = isSelectAll && someSelected;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1px 0' }}>
                <div style={{
                    width: '15px', height: '15px', border: '1.5px solid',
                    borderColor: isChecked || isIndeterminate ? '#3f88a5' : '#c4c9d4',
                    borderRadius: '3px',
                    backgroundColor: isChecked || isIndeterminate ? '#3f88a5' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    {isChecked && (
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                            <path d="M1 3.5L3.2 5.8L8 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )}
                    {isIndeterminate && !isChecked && (
                        <div style={{ width: '7px', height: '1.5px', backgroundColor: 'white', borderRadius: '1px' }} />
                    )}
                </div>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: isSelectAll ? '600' : '400' }}>
                    {option.label}
                </span>
            </div>
        );
    };

    const mergedStyles = {
        ...sStyles,
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? '#f3f8fb' : 'white',
            color: '#374151',
            cursor: 'pointer',
            padding: '7px 12px',
            ':active': { backgroundColor: '#e8f4f8' },
        }),
        multiValue: (base) => ({
            ...((sStyles?.multiValue?.(base)) || base),
            backgroundColor: allSelected ? '#e0f2fe' : '#f0f9ff',
            borderRadius: '5px',
            border: '1px solid #bae6fd',
        }),
        multiValueLabel: (base) => ({
            ...((sStyles?.multiValueLabel?.(base)) || base),
            color: '#0369a1',
            fontWeight: '500',
            fontSize: '12px',
        }),
        multiValueRemove: (base) => ({
            ...((sStyles?.multiValueRemove?.(base)) || base),
            color: '#0369a1',
            ':hover': { backgroundColor: '#bae6fd', color: '#0c4a6e' },
        }),
    };

    return (
        <Select
            isMulti
            options={enhancedOptions}
            value={displayValue}
            onChange={handleChange}
            formatOptionLabel={formatOptionLabel}
            styles={mergedStyles}
            placeholder={placeholder}
            closeMenuOnSelect={false}
            isDisabled={isDisabled}
            hideSelectedOptions={false}
        />
    );
};

const CreateGraphSidebar = ({ show, onClose, onSubmit, filterOptions, editingGraph, dataSources, selectedDataSourceType, onDataSourceChange, onPublishPreview,
    isPublishing,}) => {
    const isEditMode = !!editingGraph;
    const isSummaryEdit =   editingGraph?.representationType == 'Summary';
    const isTabularEdit =  editingGraph?.representationType === 'Tabular';

    let repType = "Graph"
    if(isSummaryEdit){
        repType = "Summary"
    }else if(isTabularEdit){
        repType = "Tabular"
    }
    const [availableMeterIds, setAvailableMeterIds] = useState([]);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [formData, setFormData] = useState({
        title: editingGraph?.title || '',
        description: editingGraph?.description || '',
        chartType: editingGraph?.chartType || 'bar',
        enableStacking: (isSummaryEdit || isTabularEdit)
            ? false
            : (editingGraph?.widgetConfig?.group_by?.length > 1) || false,
        representationType: repType,
        dataSourceType: editingGraph?.dataSourceType || 'REPORTING',
        columns: editingGraph?.widgetConfig?.group_by
            ? editingGraph.widgetConfig.group_by.map(col => {
                const normalizedValue = AXIS_LABEL_TO_VALUE_MAP[col] || col;
                return {
                    value: normalizedValue,
                    label: AXIS_FIELD_LABEL_MAP[normalizedValue] || normalizedValue
                };
            })
            : (isSummaryEdit || isTabularEdit)
                ? []
                : [{ value: 'displayPeriods', label: 'Period' }],
        widgetConfig: editingGraph?.widgetConfig || {
            module: [],
            filters: {
                financial_year: [],
                location_id: [],
                sublocation_id: [],
                category: [],
                sub_category: [],
                kpi: [],
                meter_id: [], 
                displayPeriods: [],
                startDateTime: '', 
                endDateTime: '',   
                aggregationPeriodValue: 1,
                aggregationPeriodUnit: 'hours' 
            },
            group_by: ['displayPeriods'],
            aggregate: editingGraph?.widgetConfig?.aggregate || { sum: 'value' },
            sort: [],
            limit:editingGraph?.limit,
            aggregateSubLocations: true
        }
    });

    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [availableKpis, setAvailableKpis] = useState([]);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [isPeriodDisabled, setisPeriodDisabled] = useState(false);
    const [availableSubLocations, setAvailableSubLocations] = useState([]);

    const [validationErrors, setValidationErrors] = useState({});

    const isIotMeters = selectedDataSourceType === 'IOT_METERS';

    const xAxisField = formData.columns[0]?.value || null;
    const stackByField = formData.columns[1]?.value || null;
    const isLocationInAxes = formData.columns.some(col => col.value === 'location_id');

    // ── Derived: global sort order (single source of truth) ──────────────────
    const globalSortOrder = formData.widgetConfig.sort?.[0]?.order || 'asc';

    // ── Derived: which columns are currently selected for sorting ────────────
    const sortSelectedColumns = formData.columns.filter(col =>
        Array.isArray(formData.widgetConfig.sort) &&
        formData.widgetConfig.sort.some(s => s.field === col.value)
    );

    // ── Handler: toggle global order (asc ↔ desc), applies to ALL sort fields ─
    const handleGlobalOrderToggle = (order) => {
        setFormData(prev => ({
            ...prev,
            widgetConfig: {
                ...prev.widgetConfig,
                sort: (prev.widgetConfig.sort || []).map(s => ({ ...s, order }))
            }
        }));
    };

    // ── Handler: update which columns are sorted (keeps global order) ────────
    const handleSortFieldsChange = (selected) => {
        const order = formData.widgetConfig.sort?.[0]?.order || 'asc';
        const newSort = selected.map(col => {
            const existing = Array.isArray(formData.widgetConfig.sort)
                ? formData.widgetConfig.sort.find(s => s.field === col.value)
                : null;
            return existing ? { ...existing, order } : { field: col.value, order };
        });
        setFormData(prev => ({ ...prev, widgetConfig: { ...prev.widgetConfig, sort: newSort } }));
    };

    useEffect(() => {
        if ((isEditMode && editingGraph) && filterOptions.locations) {
            if (!Array.isArray(editingGraph.widgetConfig.sort)) {
                editingGraph.widgetConfig.sort = editingGraph.widgetConfig.sort
                    ? [editingGraph.widgetConfig.sort]
                    : [];
            }

            const isEditingIot = editingGraph?.dataSourceType === 'IOT_METERS';
            const moduleArray = Array.isArray(editingGraph.widgetConfig?.module)
                ? editingGraph.widgetConfig.module.map(m => typeof m === 'string' ? { value: m, label: m } : m)
                : [];
            const financialYearArray = Array.isArray(editingGraph.widgetConfig?.filters?.financial_year)
                ? editingGraph.widgetConfig.filters.financial_year.map(f => typeof f === 'string' ? { value: f, label: f } : f)
                : [];

            const locationArray = Array.isArray(editingGraph.widgetConfig?.filters?.location_id)
                ? editingGraph.widgetConfig.filters.location_id.map(l => {
                    const locValue = typeof l === 'string' ? l : l.value;
                    const foundLocation = filterOptions?.locations?.find(loc => loc.value === locValue);
                    return foundLocation || { value: locValue, label: locValue };
                })
                : [];

            const subLocationArray = Array.isArray(editingGraph.widgetConfig?.filters?.sublocation_id)
                ? editingGraph.widgetConfig.filters.sublocation_id.map(sl => {
                    const subLocValue = typeof sl === 'string' ? sl : sl.value;
                    let foundSubLocation = null;
                    for (const location of filterOptions?.locations) {
                        if (location.subLocations && Array.isArray(location.subLocations)) {
                            foundSubLocation = location.subLocations.find(subLoc => subLoc.value === subLocValue);
                            if (foundSubLocation) break;
                        }
                    }
                    return foundSubLocation || { value: subLocValue, label: subLocValue };
                })
                : [];

            const categoryArray = Array.isArray(editingGraph.widgetConfig?.filters?.category)
                ? editingGraph.widgetConfig.filters.category.map(c => typeof c === 'string' ? { value: c, label: c } : c)
                : [];
            const subCategoryArray = Array.isArray(editingGraph.widgetConfig?.filters?.sub_category)
                ? editingGraph.widgetConfig.filters.sub_category.map(sc => typeof sc === 'string' ? { value: sc, label: sc } : sc)
                : [];
            const kpiArray = Array.isArray(editingGraph.widgetConfig?.filters?.kpi)
                ? editingGraph.widgetConfig.filters.kpi.map(k => typeof k === 'string' ? { value: k, label: k } : k)
                : [];
            const displayPeriodsArray = Array.isArray(editingGraph.widgetConfig?.filters?.displayPeriods)
                ? editingGraph.widgetConfig.filters.displayPeriods.map(p => typeof p === 'string' ? { value: p, label: p } : p)
                : [];
            const columnsArray = editingGraph.widgetConfig?.group_by
                    ? editingGraph.widgetConfig.group_by.map(col => {
                        const normalizedValue = AXIS_LABEL_TO_VALUE_MAP[col] || col;
                        return {
                            value: normalizedValue,
                            label: AXIS_FIELD_LABEL_MAP[normalizedValue] || normalizedValue
                        };
                    })
                    : []

            const meterMap = buildMeterMap(filterOptions.graphFilters);

            const meterIdArray = Array.isArray(editingGraph.widgetConfig?.filters?.meter_id)
                ? editingGraph.widgetConfig.filters.meter_id.map(m => {
                    const meterId = typeof m === 'string' ? m : m.value;
                    return {
                        value: meterId,
                        label: meterMap[meterId] ?? meterId
                    };
                })
                : [];
                    
            
            setFormData({
                title: editingGraph.title || '',
                dataSourceType: editingGraph.dataSourceType,
                description: editingGraph.description || '',
                chartType: editingGraph.chartType || 'bar',
                enableStacking: (editingGraph.widgetConfig?.group_by?.length > 1) || false,
                columns: columnsArray,
                representationType: repType,
                widgetConfig: {
                    module: moduleArray,
                    filters: {
                        financial_year: financialYearArray,
                        location_id: locationArray,
                        sublocation_id: subLocationArray,
                        category: categoryArray,
                        sub_category: subCategoryArray,
                        kpi: kpiArray,
                        ...(isEditingIot
                            ? {
                                startDateTime: editingGraph.widgetConfig?.filters?.startDateTime || '',
                                endDateTime: editingGraph.widgetConfig?.filters?.endDateTime || '',
                                aggregationPeriodValue:
                                    editingGraph.widgetConfig?.filters?.aggregationPeriodValue ?? 1,
                                aggregationPeriodUnit:
                                    editingGraph.widgetConfig?.filters?.aggregationPeriodUnit || 'hours',
                                meter_id: meterIdArray,
                                displayPeriods: []
                            }
                            : {
                                displayPeriods: displayPeriodsArray
                            })
                    },
                    group_by: editingGraph.widgetConfig?.group_by || ['displayPeriods'],
                    aggregate: editingGraph.widgetConfig?.aggregate || { sum: 'value' },
                    sort: editingGraph.widgetConfig?.sort || [{ field: 'displayPeriods', order: 'asc' }],
                    limit:editingGraph?.widgetConfig?.limit,
                    aggregateSubLocations: editingGraph.widgetConfig?.aggregateSubLocations !== undefined
                        ? editingGraph.widgetConfig.aggregateSubLocations
                        : true
                }
            });
            setValidationErrors({});
        }
    }, [isEditMode, editingGraph, filterOptions.locations]);

    const getMeterIdsForFilters = (selectedModules, selectedCategories, selectedSubCategories, selectedKpis, graphFilters) => {
        if (!graphFilters || selectedModules.length === 0) {
            return [];
        }
        
        const meterIdsSet = new Set();
        
        selectedModules.forEach(module => {
            const moduleValue = module.value || module;
            if (!graphFilters[moduleValue]) return;
            
            const categoriesToUse = selectedCategories.length > 0 
                ? selectedCategories.map(c => c.value) 
                : Object.keys(graphFilters[moduleValue]);
            
            categoriesToUse.forEach(category => {
                if (!graphFilters[moduleValue][category]) return;
                
                const allSubCategories = Object.keys(graphFilters[moduleValue][category]).filter(key => key !== 'null');
                
                const subCategoriesToUse = selectedSubCategories.length > 0
                    ? selectedSubCategories.map(sc => sc.value)
                    : [...allSubCategories, null, ''];
                
                subCategoriesToUse.forEach(subCategory => {
                    const subCatKey = (subCategory === null || subCategory === 'null') ? null : subCategory;
                    const subCatData = graphFilters[moduleValue][category][subCatKey];
                    
                    if (!subCatData) return;
                    
                    const kpisToUse = selectedKpis.length > 0
                        ? selectedKpis.map(k => k.value)
                        : Object.keys(subCatData);
                    
                    kpisToUse.forEach(kpi => {
                        const meters = subCatData[kpi];
                        if (Array.isArray(meters)) {
                            meters.forEach(meter => {
                                meterIdsSet.add(JSON.stringify({value: meter.meterId, label: `${meter.meterName} (${meter.meterId})`}));
                            });
                        }
                    });
                });
            });
        });
        
        return Array.from(meterIdsSet).map(str => JSON.parse(str));
    };

    useEffect(() => {
        if (!filterOptions.graphFilters) return;
        const selectedModules = formData.widgetConfig.module.map(m => m.value);
        if (selectedModules.length === 0) {
            setAvailableCategories([]);
            setAvailableSubCategories([]);
            setAvailableKpis([]);
            setAvailableMeterIds([]);
            return;
        }

        const categoriesSet = new Set();
        selectedModules.forEach(module => {
            if (filterOptions.graphFilters[module]) {
                Object.keys(filterOptions.graphFilters[module]).forEach(cat => {
                    categoriesSet.add(cat);
                });
            }
        });
        const categories = Array.from(categoriesSet).map(cat => ({ value: cat, label: cat }));
        setAvailableCategories(categories);
        const selectedCategories = formData.widgetConfig.filters.category.map(c => c.value);
        
        const categoriesToUse = selectedCategories.length > 0 ? selectedCategories : Array.from(categoriesSet);
        
        const subCategoriesSet = new Set();
        selectedModules.forEach(module => {
            if (filterOptions.graphFilters[module]) {
                categoriesToUse.forEach(category => {
                    if (filterOptions.graphFilters[module][category]) {
                        Object.keys(filterOptions.graphFilters[module][category]).forEach(subCat => {
                            if (subCat !== 'null' && subCat != '' && subCat !== null) {
                                subCategoriesSet.add(subCat);
                            }
                        });
                    }
                });
            }
        });
        const subCategories = Array.from(subCategoriesSet).map(subCat => ({ value: subCat, label: subCat }));
        setAvailableSubCategories(subCategories);
        
        const selectedSubCategories = formData.widgetConfig.filters.sub_category.map(sc => sc.value);
        
        const subCategoriesToUse = selectedSubCategories.length > 0 
            ? selectedSubCategories 
            : [...Array.from(subCategoriesSet), null, ''];
        
        const kpisSet = new Set();
        selectedModules.forEach(module => {
            if (filterOptions.graphFilters[module]) {
                categoriesToUse.forEach(category => {
                    if (filterOptions.graphFilters[module][category]) {
                        subCategoriesToUse.forEach(subCategory => {
                            const subCatKey = (subCategory === null || subCategory === 'null') ? null : subCategory;
                            const subCatData = filterOptions.graphFilters[module][category][subCatKey];
                            
                            if (subCatData) {
                                if (isIotMeters) {
                                    Object.keys(subCatData).forEach(kpi => {
                                        kpisSet.add(kpi);
                                    });
                                } else {
                                    if (Array.isArray(subCatData)) {
                                        subCatData.forEach(kpi => {
                                            kpisSet.add(kpi);
                                        });
                                    }
                                }
                            }
                        });
                    }
                });
            }
        });
        const kpis = Array.from(kpisSet).map(kpi => ({ value: kpi, label: kpi }));
        setAvailableKpis(kpis);

        if (isIotMeters) {
            const meterIds = getMeterIdsForFilters(
                formData.widgetConfig.module,
                formData.widgetConfig.filters.category,
                formData.widgetConfig.filters.sub_category,
                formData.widgetConfig.filters.kpi,
                filterOptions.graphFilters
            );
            setAvailableMeterIds(meterIds);
        }
    }, [
        formData.widgetConfig.module,
        formData.widgetConfig.filters.category,
        formData.widgetConfig.filters.sub_category,
        formData.widgetConfig.filters.kpi,
        formData.widgetConfig.filters.meter_id,
        filterOptions.graphFilters,
        isIotMeters
    ]);

    useEffect(() => {
        const selectedFinancialYears = formData.widgetConfig.filters.financial_year;
        if (selectedFinancialYears.length === 0 || !filterOptions.graphFilters) {
            setAvailablePeriods([]);
            return;
        }
        const periods = isIotMeters
            ? []
            : getPeriodsForFinancialYears(selectedFinancialYears, filterOptions.graphFilters);

        setAvailablePeriods(periods);
    }, [formData.widgetConfig.filters.financial_year, filterOptions.graphFilters]);

    useEffect(() => {
        const selectedLocations = formData.widgetConfig.filters.location_id;
        if (!filterOptions.locations) {
            setAvailableSubLocations([]);
            return;
        }
        const subLocations = getSubLocationsForLocations(selectedLocations, filterOptions.locations);
        setAvailableSubLocations(subLocations);
    }, [formData.widgetConfig.filters.location_id, filterOptions.locations]);

    const handleFinancialYearChange = (selected) => {
        setFormData(prev => {
            const hasPeriods = prev.widgetConfig.filters.displayPeriods.length > 0;
            const shouldReset = selected && selected.length > 0 && hasPeriods;

            return {
                ...prev,
                widgetConfig: {
                    ...prev.widgetConfig,
                    filters: {
                        ...prev.widgetConfig.filters,
                        financial_year: selected || [],
                        displayPeriods: shouldReset ? [] : prev.widgetConfig.filters.displayPeriods
                    }
                }
            };
        });
        if (validationErrors.financial_year) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.financial_year;
                return newErrors;
            });
        }
    };

    const handleLocationChange = (selected) => {
        setFormData(prev => {
            const hasSubLocations = prev.widgetConfig.filters.sublocation_id.length > 0;
            const previousLocationCount = prev.widgetConfig.filters.location_id.length;
            const newLocationCount = selected ? selected.length : 0;

            const shouldReset = hasSubLocations && (previousLocationCount !== newLocationCount);

            return {
                ...prev,
                widgetConfig: {
                    ...prev.widgetConfig,
                    filters: {
                        ...prev.widgetConfig.filters,
                        location_id: selected || [],
                        sublocation_id: shouldReset ? [] : prev.widgetConfig.filters.sublocation_id
                    }
                }
            };
        });
    };

    const handleModuleChange = (selected) => {
        const selectedValue = selected?.[0]?.value;
        const isTraining = selectedValue === 'Training';

        setisPeriodDisabled(isTraining);

        setFormData(prev => {
            const { category, sub_category, kpi } = prev.widgetConfig.filters;
            const shouldReset = selected?.length && (category.length || sub_category.length || kpi.length);

            return {
                ...prev,
                widgetConfig: {
                    ...prev.widgetConfig,
                    module: selected || [],
                    filters: {
                        ...prev.widgetConfig.filters,
                        category: shouldReset ? [] : category,
                        sub_category: shouldReset ? [] : sub_category,
                        kpi: shouldReset ? [] : kpi
                    }
                }
            };
        });

        setValidationErrors(prev => {
            const copy = { ...prev };

            if (isTraining && formData.columns.some(col => col.value === 'displayPeriods')) {
                copy.columns = 'Period option is not available for Training module';
            } else {
                delete copy.columns;
            }

            delete copy.module;
            return copy;
        });
    };

    const handleCategoryChange = (selected) => {
        setFormData(prev => {
            const hasSubCategory = prev.widgetConfig.filters.sub_category.length > 0;
            const hasKpi = prev.widgetConfig.filters.kpi.length > 0;
            const shouldReset = selected && selected.length > 0 && (hasSubCategory || hasKpi);

            return {
                ...prev,
                widgetConfig: {
                    ...prev.widgetConfig,
                    filters: {
                        ...prev.widgetConfig.filters,
                        category: selected || [],
                        sub_category: shouldReset ? [] : prev.widgetConfig.filters.sub_category,
                        kpi: shouldReset ? [] : prev.widgetConfig.filters.kpi
                    }
                }
            };
        });
    };

    const handleSubCategoryChange = (selected) => {
        setFormData(prev => {
            const hasKpi = prev.widgetConfig.filters.kpi.length > 0;
            const shouldReset = selected && selected.length > 0 && hasKpi;

            return {
                ...prev,
                widgetConfig: {
                    ...prev.widgetConfig,
                    filters: {
                        ...prev.widgetConfig.filters,
                        sub_category: selected || [],
                        kpi: shouldReset ? [] : prev.widgetConfig.filters.kpi
                    }
                }
            };
        });
    };

    const getAxisFieldOptions = () => {
        const selectedModules = formData.widgetConfig.module.map(m => m.value);
        const isTrainingSelected = selectedModules.includes("Training");
        const isIotMeters = selectedDataSourceType === 'IOT_METERS';

        const baseOptions = [
            {
                value: 'displayPeriods',
                label: 'Period',
                disabled: isTrainingSelected   
            },
            { value: 'financial_year', label: 'Financial Year' },
            { value: 'location_id', label: 'Location' },
            { value: 'category', label: 'Category' },
            { value: 'sub_category', label: 'Sub Category' },
            { value: 'kpi', label: 'KPI' }
        ];

        return isIotMeters ? [...baseOptions, {value: 'meter_id', label: 'Meter'}] : baseOptions;
    };

    const axisFieldOptions = getAxisFieldOptions();

    const validateForm = () => {
        const errors = {};
        const selectedModules = formData.widgetConfig.module.map(m => m.value);
        const isTraining = selectedModules.includes('Training');

        if (isIotMeters) {
            if (!formData.widgetConfig.filters.startDateTime) errors.startDateTime = 'Start date is required';
        }

        if (formData.representationType === 'Summary') {
            const errorsFound = {};

            if (formData.enableStacking || (formData.columns && formData.columns.length > 1)) {
                errorsFound.columns = 'Stacking is not allowed for Summary graphs';
            }

            if (!Array.isArray(formData.columns) || formData.columns.length !== 1) {
                errorsFound.columns = 'Summary graph requires exactly one column';
            } else {
                const groupField = formData.columns[0].value;
                const filters = formData.widgetConfig.filters;
                const selectedModules = formData.widgetConfig.module.map(m => m.value);

                const selectedCount =
                    groupField === 'module'
                        ? selectedModules.length
                        : (filters[groupField]?.length || 0);

                const domainCount = getDomainCountForGroupField({
                    groupField,
                    filterOptions,
                    selectedModules
                });

                if (selectedCount > 1 || (selectedCount === 0 && domainCount > 1)) {
                    errorsFound.columns =
                        `Summary graph cannot be created because ${groupField} has multiple values.`;
                }
            }

            Object.assign(errors, errorsFound);
        }

        if (formData.representationType === 'Graph') {
            if (!formData.columns || formData.columns.length === 0) {
                errors.columns = 'At least one field is required for Graph';
            }

            if (isTraining) {
                const hasPeriodColumn = formData.columns.some(col => col.value === 'displayPeriods');
                if (hasPeriodColumn) {
                    errors.columns = 'Period option is not available for Training module';
                }
            }

            if (!formData.chartType) {
                errors.chartType = 'Chart type is required';
            }
        }

        if (!formData.title || formData.title.trim() === '') {
            errors.title = 'Title is required';
        }

        if (!formData.widgetConfig.module || formData.widgetConfig.module.length === 0) {
            errors.module = 'Module is required';
        }

        if (!formData.widgetConfig.filters.financial_year || formData.widgetConfig.filters.financial_year.length === 0) {
            errors.financial_year = 'Financial year is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e, action) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        const groupByFields = formData.columns.map(c => c.value);

        const baseFilters = {
            financial_year: formData.widgetConfig.filters.financial_year.map(f => f.value || f),
            location_id: formData.widgetConfig.filters.location_id.map(l => l.value || l),
            sublocation_id: formData.widgetConfig.filters.sublocation_id.map(sl => sl.value || sl),
            category: formData.widgetConfig.filters.category.map(c => c.value || c),
            sub_category: formData.widgetConfig.filters.sub_category.map(sc => sc.value || sc),
            kpi: formData.widgetConfig.filters.kpi.map(k => k.value || k),
            displayPeriods: isIotMeters
                ? []
                : formData.widgetConfig.filters.displayPeriods.map(p => p.value)
        };

        const submissionData = {
            title: formData.title,
            description: formData.description,
            chartType: formData.chartType,
            representationType: formData.representationType,
            dataSourceType: selectedDataSourceType,
            widgetConfig: {
                ...formData.widgetConfig,
                module: formData.widgetConfig.module.map(m => m.value || m),
                filters: {
                    ...baseFilters,
                    ...(isIotMeters && {
                        startDateTime: formData.widgetConfig.filters.startDateTime,
                        endDateTime: formData.widgetConfig.filters.endDateTime,
                        aggregationPeriodValue: formData.widgetConfig.filters.aggregationPeriodValue,
                        aggregationPeriodUnit: formData.widgetConfig.filters.aggregationPeriodUnit,
                        meter_id: formData.widgetConfig.filters.meter_id?.map(m => m.value || m) || [],
                        displayPeriods: []
                    })
                },
                ...(isIotMeters && {
                    meters: Object.fromEntries(
                        (formData.widgetConfig.filters.meter_id ?? [])
                            .map(m => [m.value, m.label])
                    )
                }),
                group_by: groupByFields,
                sort:  Array.isArray(formData?.widgetConfig?.sort) ? formData?.widgetConfig?.sort : [formData?.widgetConfig?.sort] ,
                limit:formData?.widgetConfig?.limit,
                aggregateSubLocations: formData.widgetConfig.aggregateSubLocations
            }
        };

        onSubmit(submissionData, action);
    };

    const handleStackingToggle = (checked) => {
        if (formData.representationType === "Graph") {
            setFormData(prev => ({
                ...prev,
                enableStacking: checked,
                columns: checked ? prev.columns : prev.columns.slice(0, 1)
            }));
        }
    };

    const handleColumnChange = (selected) => {
        const newColumns = selected || [];

        setFormData(prev => {
            const newColumnValues = newColumns.map(col => col.value);

            const updatedSort = (prev.widgetConfig.sort || []).filter(s =>
                newColumnValues.includes(s.field) || s.field === 'value'
            );

            if (updatedSort.length === 0 && newColumnValues.length > 0) {
                updatedSort.push({ field: newColumnValues[0], order: 'asc' });
            }

            return {
                ...prev,
                columns: newColumns,
                enableStacking: newColumns.length > 1 ? prev.enableStacking : false,
                widgetConfig: {
                    ...prev.widgetConfig,
                    sort: updatedSort,
                    aggregateSubLocations: newColumns.some(col => col.value === 'location_id')
                        ? true
                        : prev.widgetConfig.aggregateSubLocations
                }
            };
        });
    };

    const resetForRepresentationType = (repType) => {
        switch (repType) {
            case "Graph":
                return { columns: [], enableStacking: false };
            case "Summary":
                return { columns: [], enableStacking: false };
            case "Tabular":
                return { columns: [], enableStacking: false };
            default:
                return { columns: [], enableStacking: false };
        }
    };

    useEffect(() => {
        setFormData(prev => {
            const reset = resetForRepresentationType(prev.representationType);
            return { ...prev, ...reset };
        });

        setValidationErrors(prev => {
            const copy = { ...prev };
            delete copy.columns;
            delete copy.chartType;
            return copy;
        });
    }, [formData.representationType]);

    const availableRepresentationTypes = React.useMemo(() => {
        return RepresentationTypeList;
    }, [selectedDataSourceType]);

    useEffect(() => {
        if (!show) {
            setValidationErrors({});
        }
    }, [show]);

    if (!show) return null;

    const canShowFilters = selectedDataSourceType !== null;

    const filterBadgeStyle = (count) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '18px',
        height: '18px',
        padding: '0 5px',
        borderRadius: '9px',
        backgroundColor: count > 0 ? '#3f88a5' : '#e5e7eb',
        color: count > 0 ? 'white' : '#9ca3af',
        fontSize: '11px',
        fontWeight: '600',
        marginLeft: '6px',
    });

    const filterLabelStyle = {
        ...styles.formLabel,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    };

    // ── Advanced options badge count ─────────────────────────────────────────
    const advancedBadgeCount = (Array.isArray(formData.widgetConfig.sort) ? formData.widgetConfig.sort.length : 0);

    return (
        <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
                <h3 style={styles.sidebarTitle}>
                    {isEditMode ? 'Edit Graph' : 'Create New Graph'}
                </h3>
                <button
                    style={{ ...styles.iconButton, width: '32px', height: '32px' }}
                    onClick={onClose}
                    title="Close"
                    onMouseOver={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                        e.currentTarget.style.borderColor = '#fecaca';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                >
                    <X size={18} color="#6b7280" />
                </button>
            </div>

            <div style={styles.sidebarContent}>
                <div>
                    <div style={styles.formGroup}>
                        <label style={styles.formLabel}>
                            Data Source<span style={styles.requiredIndicator}>*</span>
                        </label>
                        <select
                            style={validationErrors.dataSource ? styles.formInputError : styles.formInput}
                            value={selectedDataSourceType || ''}
                            onChange={(e) => onDataSourceChange(e.target.value)}
                            required
                            disabled={isEditMode}
                        >
                            <option value="">Select a Data Source</option>
                            {isEditMode && <option value={selectedDataSourceType}>{selectedDataSourceType}</option>}
                            {!isEditMode && dataSources.map(ds => (
                                <option key={ds.type} value={ds.type}>{ds.type}</option>
                            ))}
                        </select>
                        {validationErrors.dataSource && (
                            <span style={styles.errorMessage}>{validationErrors.dataSource}</span>
                        )}
                        <div style={styles.infoText}>
                            {isEditMode 
                                ? 'Data source cannot be changed when editing'
                                : 'Select a data source to enable graph configuration'
                            }
                        </div>
                    </div>

                { canShowFilters && (
                    <>
                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>
                                Title<span style={styles.requiredIndicator}>*</span>
                            </label>
                            <input
                                type="text"
                                style={validationErrors.title ? styles.formInputError : styles.formInput}
                                value={formData.title}
                                onChange={(e) => {
                                    setFormData({ ...formData, title: e.target.value });
                                    if (validationErrors.title) {
                                        setValidationErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.title;
                                            return newErrors;
                                        });
                                    }
                                }}
                                required
                                placeholder="Enter graph title"
                            />
                            {validationErrors.title && (
                                <span style={styles.errorMessage}>{validationErrors.title}</span>
                            )}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Description</label>
                            <textarea
                                style={styles.formTextarea}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Enter graph description"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Representation Type</label>
                            <select
                                style={styles.formInput}
                                value={formData.representationType}
                                onChange={(e) =>
                                    setFormData({ ...formData, representationType: e.target.value })
                                }
                            >
                                {availableRepresentationTypes.map((data) => (
                                    <option key={data.value} value={data.value}>
                                        {data.label}
                                    </option>
                                ))}
                            </select>
                            <div style={styles.infoText}>
                                Select how the data should be presented upon publishing
                            </div>
                        </div>

                        {formData.representationType === "Graph" && <div style={styles.formGroup}>
                            <label style={styles.formLabel}>
                                Chart Type<span style={styles.requiredIndicator}>*</span>
                            </label>
                            <select
                                style={validationErrors.chartType ? styles.formInputError : styles.formInput}
                                value={formData.chartType}
                                onChange={(e) => {
                                    setFormData({ ...formData, chartType: e.target.value });
                                    if (validationErrors.chartType) {
                                        setValidationErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.chartType;
                                            return newErrors;
                                        });
                                    }
                                }}
                                required
                            >
                                <option value="column">Column Chart</option>
                                <option value="bar">Bar Chart</option>
                                <option value="line">Line Chart</option>
                                <option value="area">Area Chart</option>
                                <option value="pie">Pie Chart</option>
                                <option value="donut">Donut Chart</option>
                            </select>
                            {validationErrors.chartType && (
                                <span style={styles.errorMessage}>{validationErrors.chartType}</span>
                            )}
                        </div>}

                        <div style={styles.sectionHeader}>Chart Configuration</div>

                        {formData.representationType === "Graph" && (
                            <>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>
                                        X-Axis Field<span style={styles.requiredIndicator}>*</span>
                                    </label>
                                    <select
                                        style={validationErrors.columns ? styles.formInputError : styles.formInput}
                                        value={xAxisField || ''}
                                        onChange={(e) => {
                                            const selectedOption = axisFieldOptions.find(opt => opt.value === e.target.value);
                                            if (selectedOption) {
                                                const newColumns = [selectedOption];
                                                if (formData.columns[1]) {
                                                    newColumns.push(formData.columns[1]);
                                                }
                                                handleColumnChange(newColumns);
                                            }
                                        }}
                                    >
                                        <option value="">Select X-Axis Field</option>
                                        {axisFieldOptions.map(opt => (
                                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {validationErrors.columns && (
                                        <span style={styles.errorMessage}>{validationErrors.columns}</span>
                                    )}
                                </div>

                                <div style={styles.formGroup}>
                                    <div style={styles.checkboxGroup}>
                                        <input
                                            type="checkbox"
                                            id="enableStacking"
                                            style={styles.checkbox}
                                            checked={formData.enableStacking}
                                            onChange={(e) => handleStackingToggle(e.target.checked)}
                                            disabled={!xAxisField}
                                        />
                                        <label htmlFor="enableStacking" style={styles.checkboxLabel}>
                                            Enable Stacking
                                        </label>
                                    </div>
                                    <div style={styles.infoText}>
                                        {!xAxisField 
                                            ? 'Select X-Axis field first to enable stacking' 
                                            : 'Stack data by another dimension for comparative analysis'}
                                    </div>
                                </div>

                                {formData.enableStacking && (
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Stack By Field *</label>
                                        <select
                                            style={styles.formInput}
                                            value={stackByField || ''}
                                            onChange={(e) => {
                                                const selectedOption = axisFieldOptions.find(opt => opt.value === e.target.value);
                                                if (selectedOption && formData.columns[0]) {
                                                    handleColumnChange([formData.columns[0], selectedOption]);
                                                }
                                            }}
                                            required={formData.enableStacking}
                                        >
                                            <option value="">Select field to stack by</option>
                                            {axisFieldOptions
                                                .filter(opt => opt.value !== xAxisField)
                                                .map(opt => (
                                                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                        </select>
                                        <div style={styles.infoText}>Data will be grouped and stacked by this field</div>
                                    </div>
                                )}
                            </>
                        )}

                        {formData.representationType === "Summary" && (
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    Field<span style={styles.requiredIndicator}>*</span>
                                </label>
                                <Select
                                    isMulti={false}
                                    options={axisFieldOptions.map(opt => ({
                                        value: opt.value,
                                        label: opt.label,
                                        isDisabled: opt.disabled
                                    }))}
                                    value={formData.columns[0] || null}
                                    onChange={(selected) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            columns: selected ? [selected] : []
                                        }));
                                    }}
                                    closeMenuOnSelect
                                    styles={getCustomSelectStyles(validationErrors.columns)}
                                    placeholder="Select a field"
                                />
                                {validationErrors.columns && (
                                    <span style={styles.errorMessage}>{validationErrors.columns}</span>
                                )}
                            </div>
                        )}

                        {formData.representationType === "Tabular" && (
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>
                                    Fields<span style={styles.requiredIndicator}>*</span>
                                </label>
                                <Select
                                    isMulti
                                    options={axisFieldOptions.map(opt => ({
                                        value: opt.value,
                                        label: opt.label,
                                        isDisabled: opt.disabled
                                    }))}
                                    value={formData.columns}
                                    onChange={(selected) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            columns: selected || []
                                        }));
                                    }}
                                    closeMenuOnSelect={false}
                                    styles={getCustomSelectStyles(validationErrors.columns)}
                                    placeholder="Select Field(s)"
                                />
                                {validationErrors.columns && (
                                    <span style={styles.errorMessage}>{validationErrors.columns}</span>
                                )}
                            </div>
                        )}

                        {isLocationInAxes && (
                            <div style={styles.formGroup}>
                                <div style={styles.checkboxGroup}>
                                    <input
                                        type="checkbox"
                                        id="aggregateSubLocations"
                                        style={styles.checkbox}
                                        checked={formData.widgetConfig.aggregateSubLocations}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setFormData(prev => ({
                                                ...prev,
                                                widgetConfig: {
                                                    ...prev.widgetConfig,
                                                    aggregateSubLocations: checked
                                                }
                                            }))
                                        }}
                                    />
                                    <label htmlFor="aggregateSubLocations" style={styles.checkboxLabel}>
                                        Aggregate SubLocations
                                    </label>
                                </div>
                                <div style={styles.infoText}>
                                    When enabled, location values will include aggregated data from all sublocations
                                </div>
                            </div>
                        )}

                        <div style={styles.sectionHeader}>Filters</div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>
                                Module<span style={styles.requiredIndicator}>*</span>
                            </label>
                            <select
                                style={validationErrors.module ? styles.formInputError : styles.formInput}
                                value={formData.widgetConfig.module.length > 0 ? formData.widgetConfig.module[0].value : ''}
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    if (selectedValue) {
                                        if (selectedValue === "Training") {
                                            setisPeriodDisabled(true)
                                        }
                                        handleModuleChange([{ value: selectedValue, label: selectedValue }]);
                                    } else {
                                        handleModuleChange([]);
                                    }
                                }}
                                required
                            >
                                <option value="">Select a Module</option>
                                {filterOptions.modules && filterOptions.modules.map(module => (
                                    <option key={module.value} value={module.value}>
                                        {module.label}
                                    </option>
                                ))}
                            </select>
                            {validationErrors.module && (
                                <span style={styles.errorMessage}>{validationErrors.module}</span>
                            )}
                            <div style={styles.infoText}>Select a module to enable category selection</div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                Category
                                {formData.widgetConfig.filters.category.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.category.length)}>
                                        {formData.widgetConfig.filters.category.length === availableCategories.length && availableCategories.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.category.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={availableCategories}
                                value={formData.widgetConfig.filters.category}
                                onChange={handleCategoryChange}
                                styles={customSelectStyles}
                                placeholder="Select Categories"
                                allLabel="All Categories"
                            />
                            <div style={styles.infoText}>Available based on selected modules</div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                Sub Category
                                {formData.widgetConfig.filters.sub_category.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.sub_category.length)}>
                                        {formData.widgetConfig.filters.sub_category.length === availableSubCategories.length && availableSubCategories.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.sub_category.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={availableSubCategories}
                                value={formData.widgetConfig.filters.sub_category}
                                onChange={handleSubCategoryChange}
                                styles={customSelectStyles}
                                placeholder="Select Sub Categories"
                                allLabel="All Sub Categories"
                            />
                            <div style={styles.infoText}>Available based on selected categories</div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                KPI
                                {formData.widgetConfig.filters.kpi.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.kpi.length)}>
                                        {formData.widgetConfig.filters.kpi.length === availableKpis.length && availableKpis.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.kpi.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={availableKpis}
                                value={formData.widgetConfig.filters.kpi}
                                onChange={(selected) => setFormData({
                                    ...formData,
                                    widgetConfig: {
                                        ...formData.widgetConfig,
                                        filters: { ...formData.widgetConfig.filters, kpi: selected || [] }
                                    }
                                })}
                                styles={customSelectStyles}
                                placeholder="Select KPIs"
                                allLabel="All KPIs"
                            />
                            <div style={styles.infoText}>Available based on selected sub categories</div>
                        </div>

                        {isIotMeters && (
                            <div style={styles.formGroup}>
                                <label style={filterLabelStyle}>
                                    Meter ID
                                    {formData.widgetConfig.filters.meter_id?.length > 0 && (
                                        <span style={filterBadgeStyle(formData.widgetConfig.filters.meter_id.length)}>
                                            {formData.widgetConfig.filters.meter_id.length === availableMeterIds.length && availableMeterIds.length > 0
                                                ? 'All'
                                                : formData.widgetConfig.filters.meter_id.length}
                                        </span>
                                    )}
                                </label>
                                <SelectWithAll
                                    options={availableMeterIds}
                                    value={formData.widgetConfig.filters.meter_id}
                                    onChange={(selected) => setFormData(prev => ({
                                        ...prev,
                                        widgetConfig: {
                                            ...prev.widgetConfig,
                                            filters: { ...prev.widgetConfig.filters, meter_id: selected || [] }
                                        }
                                    }))}
                                    styles={customSelectStyles}
                                    placeholder="Select Meter IDs"
                                    allLabel="All Meters"
                                />
                            </div>
                        )}

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                Financial Year<span style={styles.requiredIndicator}>*</span>
                                {formData.widgetConfig.filters.financial_year.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.financial_year.length)}>
                                        {formData.widgetConfig.filters.financial_year.length === filterOptions.financialYears?.length && filterOptions.financialYears?.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.financial_year.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={filterOptions.financialYears}
                                value={formData.widgetConfig.filters.financial_year}
                                onChange={handleFinancialYearChange}
                                styles={getCustomSelectStyles(validationErrors.financial_year)}
                                placeholder="Select Financial Years"
                                allLabel="All Financial Years"
                            />
                            {validationErrors.financial_year && (
                                <span style={styles.errorMessage}>{validationErrors.financial_year}</span>
                            )}
                            <div style={styles.infoText}>Select financial years to enable period selection</div>
                        </div>

                        {!isIotMeters && (
                            <div style={styles.formGroup}>
                                <label style={filterLabelStyle}>
                                    Period
                                    {formData.widgetConfig.filters.displayPeriods.length > 0 && (
                                        <span style={filterBadgeStyle(formData.widgetConfig.filters.displayPeriods.length)}>
                                            {formData.widgetConfig.filters.displayPeriods.length === availablePeriods.length && availablePeriods.length > 0
                                                ? 'All'
                                                : formData.widgetConfig.filters.displayPeriods.length}
                                        </span>
                                    )}
                                </label>
                                <SelectWithAll
                                    options={availablePeriods}
                                    isDisabled={isPeriodDisabled}
                                    value={formData.widgetConfig.filters.displayPeriods}
                                    onChange={(selected) => setFormData({
                                        ...formData,
                                        widgetConfig: {
                                            ...formData.widgetConfig,
                                            filters: { ...formData.widgetConfig.filters, displayPeriods: selected || [] }
                                        }
                                    })}
                                    styles={customSelectStyles}
                                    placeholder="Select Periods"
                                    allLabel="All Periods"
                                />
                                <div style={styles.infoText}>
                                    {isPeriodDisabled
                                        ? 'Showing unique trainee headcount for the entire financial year'
                                        : 'Showing periods based on selected financial years'}
                                </div>
                            </div>
                        )}

                        {isIotMeters && (
                            <>
                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>
                                        Start Date / Time<span style={styles.requiredIndicator}>*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        style={styles.formInput}
                                        value={formData.widgetConfig.filters.startDateTime}
                                        onChange={(e) => {
                                            const newVal = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                widgetConfig: {
                                                    ...prev.widgetConfig,
                                                    filters: {
                                                        ...prev.widgetConfig.filters,
                                                        startDateTime: newVal
                                                    }
                                                }
                                            }));
                                        }}
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.formLabel}>
                                        End Date / Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        style={styles.formInput}
                                        value={formData.widgetConfig.filters.endDateTime}
                                        onChange={(e) => {
                                            const newVal = e.target.value || null;
                                            setFormData(prev => ({
                                                ...prev,
                                                widgetConfig: {
                                                    ...prev.widgetConfig,
                                                    filters: {
                                                        ...prev.widgetConfig.filters,
                                                        endDateTime: newVal
                                                    }
                                                }
                                            }));
                                        }}
                                    />
                                </div>

                                {xAxisField === 'displayPeriods' && (
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Aggregation Period</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="number"
                                                min={1}
                                                value={formData.widgetConfig.filters.aggregationPeriodValue}
                                                onChange={(e) => {
                                                    const newVal = Number(e.target.value);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        widgetConfig: {
                                                            ...prev.widgetConfig,
                                                            filters: {
                                                                ...prev.widgetConfig.filters,
                                                                aggregationPeriodValue: newVal
                                                            }
                                                        }
                                                    }));
                                                }}
                                                style={{ ...styles.formInput, width: '80px' }}
                                            />

                                            <select
                                                style={styles.formInput}
                                                value={formData.widgetConfig.filters.aggregationPeriodUnit}
                                                onChange={(e) => {
                                                    const newVal = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        widgetConfig: {
                                                            ...prev.widgetConfig,
                                                            filters: {
                                                                ...prev.widgetConfig.filters,
                                                                aggregationPeriodUnit: newVal
                                                            }
                                                        }
                                                    }));
                                                }}
                                            >
                                                {Array.isArray(AggreGatePeriodList) &&
                                                    AggreGatePeriodList.map((data) => (
                                                        <option key={data.value} value={data.value}>
                                                            {data.label}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                Location
                                {formData.widgetConfig.filters.location_id.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.location_id.length)}>
                                        {formData.widgetConfig.filters.location_id.length === filterOptions.locations?.length && filterOptions.locations?.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.location_id.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={filterOptions.locations}
                                value={formData.widgetConfig.filters.location_id}
                                onChange={handleLocationChange}
                                styles={customSelectStyles}
                                placeholder="Select Locations"
                                allLabel="All Locations"
                            />
                            <div style={styles.infoText}>Leave empty to select all locations</div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={filterLabelStyle}>
                                Sub Locations
                                {formData.widgetConfig.filters.sublocation_id.length > 0 && (
                                    <span style={filterBadgeStyle(formData.widgetConfig.filters.sublocation_id.length)}>
                                        {formData.widgetConfig.filters.sublocation_id.length === availableSubLocations.length && availableSubLocations.length > 0
                                            ? 'All'
                                            : formData.widgetConfig.filters.sublocation_id.length}
                                    </span>
                                )}
                            </label>
                            <SelectWithAll
                                options={availableSubLocations}
                                value={formData.widgetConfig.filters.sublocation_id}
                                onChange={(selected) => setFormData({
                                    ...formData,
                                    widgetConfig: {
                                        ...formData.widgetConfig,
                                        filters: { ...formData.widgetConfig.filters, sublocation_id: selected || [] }
                                    }
                                })}
                                styles={customSelectStyles}
                                placeholder="Select Sub Locations"
                                allLabel="All Sub Locations"
                            />
                            <div style={styles.infoText}>
                                {formData.widgetConfig.filters.location_id.length === 0
                                    ? "Showing all sublocations (all locations selected)"
                                    : "Showing sublocations for selected locations"}
                            </div>
                        </div>

                        {/* ── ADVANCED OPTIONS ──────────────────────────────────────────────── */}
                        <div
                            style={{
                                marginTop: '16px',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                overflow: 'visible',  // allow dropdown to overflow
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '9px 12px',
                                    backgroundColor: '#f9fafb',
                                    borderBottom: showAdvanced ? '1px solid #e5e7eb' : 'none',
                                    borderRadius: showAdvanced ? '8px 8px 0 0' : '8px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Advanced Options</span>
                                    {advancedBadgeCount > 0 && (
                                        <span style={{
                                            fontSize: '10px', padding: '1px 6px', borderRadius: '10px',
                                            backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: '600'
                                        }}>
                                            {advancedBadgeCount} sort{advancedBadgeCount > 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAdvanced(prev => !prev)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        padding: '3px 9px', borderRadius: '5px', cursor: 'pointer',
                                        fontSize: '11px', fontWeight: '600', lineHeight: 1,
                                        backgroundColor: showAdvanced ? '#e0f2fe' : '#f3f4f6',
                                        color: showAdvanced ? '#0369a1' : '#6b7280',
                                        border: `1px solid ${showAdvanced ? '#bae6fd' : '#e5e7eb'}`,
                                        outline: 'none', transition: 'all 0.15s ease',
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = showAdvanced ? '#bae6fd' : '#e5e7eb'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = showAdvanced ? '#e0f2fe' : '#f3f4f6'; }}
                                >
                                    {showAdvanced ? 'Hide' : 'Show'}
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                        style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                                    >
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </button>
                            </div>

                            {showAdvanced && (
                                <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '0px', overflow: 'visible' }}>

                                    {/* ── 1. SORT BY (first) ──────────────────────────────────────── */}
                                    <div style={{ ...styles.formGroup, marginBottom: '14px' }}>
                                        {/* Label row: title + global ASC/DESC toggle */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <label style={{ ...styles.formLabel, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                Sort By
                                                {sortSelectedColumns.length > 0 && (
                                                    <span style={{
                                                        fontSize: '10px', padding: '1px 6px', borderRadius: '10px',
                                                        backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: '600',
                                                    }}>
                                                        {sortSelectedColumns.length} field{sortSelectedColumns.length > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </label>

                                            {/* Global ASC / DESC segmented control */}
                                            <div style={{
                                                display: 'flex',
                                                borderRadius: '6px',
                                                overflow: 'hidden',
                                                border: '1px solid #e5e7eb',
                                                flexShrink: 0,
                                            }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleGlobalOrderToggle('asc')}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '3px',
                                                        padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                                        cursor: 'pointer', border: 'none', outline: 'none', lineHeight: 1,
                                                        backgroundColor: globalSortOrder === 'asc' ? '#3f88a5' : 'white',
                                                        color: globalSortOrder === 'asc' ? 'white' : '#6b7280',
                                                        transition: 'all 0.1s ease',
                                                    }}
                                                    title="Sort Ascending"
                                                >
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                                                    </svg>
                                                    ASC
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleGlobalOrderToggle('desc')}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '3px',
                                                        padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                                        cursor: 'pointer', border: 'none', borderLeft: '1px solid #e5e7eb',
                                                        outline: 'none', lineHeight: 1,
                                                        backgroundColor: globalSortOrder === 'desc' ? '#3f88a5' : 'white',
                                                        color: globalSortOrder === 'desc' ? 'white' : '#6b7280',
                                                        transition: 'all 0.1s ease',
                                                    }}
                                                    title="Sort Descending"
                                                >
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>
                                                    </svg>
                                                    DESC
                                                </button>
                                            </div>
                                        </div>

                                        {/* Custom dropdown */}
                                        <SortFieldDropdown
                                            options={formData.columns}
                                            value={sortSelectedColumns}
                                            onChange={handleSortFieldsChange}
                                            disabled={formData.columns.length === 0}
                                        />

                                        <div style={styles.infoText}>
                                            {formData.columns.length === 0
                                                ? 'Select fields in Chart Configuration first'
                                                : 'Choose which fields to sort by. ASC / DESC applies to all selected fields.'}
                                        </div>

                                        {/* Selected sort chips */}
                                        {sortSelectedColumns.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                                                {sortSelectedColumns.map((col, idx) => (
                                                    <span key={col.value} style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                        padding: '2px 8px 2px 6px',
                                                        borderRadius: '20px',
                                                        background: '#f0f9ff',
                                                        border: '1px solid #bae6fd',
                                                        fontSize: '11px', fontWeight: 500, color: '#0369a1',
                                                    }}>
                                                        <span style={{
                                                            width: 16, height: 16, borderRadius: '50%',
                                                            background: '#3f88a5', color: 'white',
                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '9px', fontWeight: 700, flexShrink: 0,
                                                        }}>
                                                            {idx + 1}
                                                        </span>
                                                        {col.label}
                                                        <span style={{
                                                            marginLeft: '2px', fontSize: '9px', fontWeight: 700,
                                                            color: '#0369a1', opacity: 0.7, textTransform: 'uppercase',
                                                        }}>
                                                            {globalSortOrder}
                                                        </span>
                                                        {/* Remove chip */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSortFieldsChange(sortSelectedColumns.filter(c => c.value !== col.value))}
                                                            style={{
                                                                marginLeft: '1px', background: 'none', border: 'none',
                                                                cursor: 'pointer', padding: 0, lineHeight: 1,
                                                                color: '#0369a1', display: 'flex', alignItems: 'center',
                                                            }}
                                                            title={`Remove ${col.label}`}
                                                        >
                                                            <X size={10} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Divider */}
                                    <div style={{ height: '1px', background: '#f3f4f6', margin: '2px 0 14px' }} />

                                    {/* ── 2. OPERATION ───────────────────────────────────────────── */}
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>
                                            Operation (Aggregation)<span style={styles.requiredIndicator}>*</span>
                                        </label>
                                        <select
                                            style={styles.formInput}
                                            value={Object.keys(formData.widgetConfig.aggregate)[0] || 'sum'}
                                            onChange={(e) => {
                                                const op = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    widgetConfig: {
                                                        ...prev.widgetConfig,
                                                        aggregate: { [op]: 'value' }
                                                    }
                                                }));
                                            }}
                                        >
                                            {OPERATION_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <div style={styles.infoText}>Mathematical operation applied to the metric values</div>
                                    </div>

                                    {/* ── 3. RESULT LIMIT ────────────────────────────────────────── */}
                                    <div style={styles.formGroup}>
                                        <label style={styles.formLabel}>Result Limit</label>
                                        <input
                                            type="number"
                                            min={1}
                                            placeholder="No limit"
                                            value={formData.widgetConfig.limit ?? ""}
                                            style={styles.formInput}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    widgetConfig: {
                                                        ...prev.widgetConfig,
                                                        limit: raw === "" ? null : Math.max(1, Number(raw))
                                                    }
                                                }));
                                            }}
                                        />
                                        <div style={styles.infoText}>Maximum number of results to display</div>
                                    </div>

                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                marginTop: "20px",
                                paddingTop: "15px",
                                borderTop: "1px solid #e5e7eb",
                                flexWrap: "wrap"
                            }}
                        >
                            <button
                                type="button"
                                style={{
                                    ...styles.submitButton,
                                    backgroundColor: "#16a34a",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    opacity: isPublishing ? 0.7 : 1
                                }}
                                disabled={isPublishing}
                                onClick={(e) => {
                                    if (isEditMode) {
                                        handleSubmit(e, "SAVE_CHANGES");
                                    } else {
                                        onPublishPreview();
                                    }
                                }}
                            >
                                {isPublishing ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {isEditMode ? "Saving..." : "Publishing..."}
                                    </>
                                ) : (
                                    isEditMode ? "Save & Close" : "Publish & Close"
                                )}
                            </button>

                            <button
                                type="button"
                                style={{
                                    ...styles.submitButton,
                                    padding: "8px 16px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    height: "38px",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    whiteSpace: "nowrap",
                                    flex: "1 1 auto",
                                    minWidth: "120px",
                                    backgroundColor: "#3f88a5",
                                    color: "white",
                                    border: "none",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                                }}
                                onClick={(e) => handleSubmit(e, "CREATE_PREVIEW")}
                            >
                                {isEditMode ? "Update Preview" : "Create Preview"}
                            </button>
                        </div>
                    </>
                )}
                </div>
            </div>
        </div>
    );
};


const BiDashboard = ({ isOpen, onClose, editingGraph, onPublish }) => {
    const [graphs, setGraphs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isMinimized, setIsMinimized] = useState(false);
    const [showCreateSidebar, setShowCreateSidebar] = useState(true);
    const [dataSources, setDataSources] = useState([]);
    const [selectedDataSourceType, setSelectedDataSourceType] = useState(null);
    const [isPublishing, setisPublishing] = useState(false);

    const [filterOptions, setFilterOptions] = useState({
        modules: [],
        financialYears: [],
        locations: [],
        graphFilters: null
    });

    const fetchDataSources = useCallback(async () => {
        try {
            if (editingGraph && editingGraph.dataSourceType) {
                setSelectedDataSourceType(editingGraph.dataSourceType);
                return;
            }

            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}dashboard/data-sources`,
                {},
                {},
                "GET"
            );

            if (isSuccess && data) {
                setDataSources(data);
                if (!editingGraph && data.length > 0) {
                    setSelectedDataSourceType(data[0].type);
                }
            }
        } catch (error) {
            console.error("Failed to load data sources:", error);
        }
    }, [editingGraph]);

    const fetchFilterOptions = useCallback(async (dataSourceType) => {
        if (!dataSourceType) return;
        
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/filters`,
                {},
                { dataSource: dataSourceType },
                "GET"
            );

            if (isSuccess && data.data) {
                const graphFilters = data.data;
                const modules = Object.keys(graphFilters)
                    .filter(key => key !== 'Financial Years' && key !== 'Location')
                    .map(module => ({ value: module, label: module }));

                const financialYearsData = graphFilters['Financial Years'] || {};
                
                const isIotMeters = dataSourceType === 'IOT_METERS';
                
                let financialYears;
                if (isIotMeters) {
                    financialYears = Array.isArray(financialYearsData) 
                        ? financialYearsData 
                        : [];
                } else {
                    financialYears = Object.keys(financialYearsData).map(fy => ({
                        value: fy,
                        label: fy
                    }));
                }

                const locations = graphFilters['Location'] || [];

                setFilterOptions({
                    modules: modules,
                    financialYears: financialYears,
                    locations: locations,
                    graphFilters: graphFilters
                });
            }
        } catch (error) {
            console.error("Failed to load filter options:", error);
        }
    }, [dataSources]);

    const handleDataSourceChange = useCallback((dataSourceType) => {
        setSelectedDataSourceType(dataSourceType);
        if (dataSourceType) {
            fetchFilterOptions(dataSourceType);
        } else {
            setFilterOptions({
                modules: [],
                financialYears: [],
                locations: [],
                graphFilters: null
            });
        }
    }, [fetchFilterOptions]);

    const getGraphData = useCallback(async (graphId, appliedFilters = {}) => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/${graphId}/data`,
                {},
                appliedFilters,
                "GET"
            );
            if (isSuccess && Array.isArray(data?.data)) {
                return data.data;
            }
        } catch (error) {
            console.error("Error fetching graph data:", error);
        }
        return [];
    }, []);

    const syncGraph = useCallback(async (graphId) => {
        setError("");
        try {
            const { isSuccess: dataSuccess, message: message, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/${graphId}/sync`,
                {},
                {},
                "GET"
            );
            if (dataSuccess && data?.data) {
                const graph = graphs.find(graph => graph.id === graphId);
                if (dataSuccess && Array.isArray(data?.data)) {
                    setGraphs(prev =>
                        prev.map(g =>
                            g.id === graphId
                                ? {
                                    ...g,
                                    chartData: {
                                        ...g.chartData,
                                        data: data?.data
                                    }
                                }
                                : g
                        )
                    );
                }
            } else {
                setError(message || "Failed to sync graph");
            }
        } catch (error) {
            setError("Failed to sync graph");
        }
    }, [graphs, selectedDataSourceType]);

    const publishGraph = useCallback(async (graph) => {
        setError("");
        setisPublishing(true)

        const source = graph.payload ?? graph;
        const chartData = graph?.chartData
        if (chartData?.data?.length && chartData?.data?.length > 1) {
            if (source.representationType === 'Summary') {
                setisPublishing(false)
                return sweetAlert("error", "Cannot Published this as Summary", false)
            }
        }
        if (!source?.widgetConfig) {
            setError("Invalid graph configuration");
            setisPublishing(false)
            return;
        }
        
        const body = {
            title: source.title,
            description: source.description,
            chartType: source.chartType,
            representationType: source.representationType,
            dataSourceType: source.dataSourceType,
            widgetConfig: source.widgetConfig,
        };

        try {
            const { isSuccess, data, message } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/publish`,
                {},
                body,
                "POST"
            );
           
            if (isSuccess) {
                sweetAlert("success", "Graph published successfully", false);
                
                const graphToPublish = {
                    ...data.data,
                    chartData: chartData,
                    representationType: source.representationType,
                };
                onPublish?.(graphToPublish);
                
                onClose(false);
            } else {
                setError(message || "Failed to publish graph");
            }
            setisPublishing(false)
        } catch (err) {
            console.error(err);
            setError("An error occurred during publishing");
            setisPublishing(false)
        }
    }, [onClose, selectedDataSourceType]);

    const publishLatestPreview = useCallback(async () => {
        if (!graphs || graphs.length === 0) {
            sweetAlert("error", "Create preview before publishing", false);
            return;
        }

        const previewGraph = graphs.find(g => g.id === "preview");

        if (!previewGraph) {
            sweetAlert("error", "No preview graph available", false);
            return;
        }

        await publishGraph(previewGraph);
    }, [graphs, publishGraph]);

    const updateGraph = useCallback(async (graphConfig) => {
        setLoading(true);
        setError("");

        try {
            const { isSuccess, data, message } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/${editingGraph.id}`,
                {},
                graphConfig,
                "PUT",
                false,
                false
            );

            if (!isSuccess || !data.data) {
                setError(message || "Failed to update graph");
                return;
            }

            const graphData = await getGraphData(editingGraph.id);
            const updatedGraph = {
                ...data.data,
                id: editingGraph.id,
                representationType: graphConfig.representationType,
                chartType: graphConfig.chartType,
                widgetConfig: graphConfig.widgetConfig,
                title: graphConfig.title,
                description: graphConfig.description,
                chartData: {
                    data: Array.isArray(graphData) ? graphData : [],
                    chartType: graphConfig.chartType,
                    xAxisField: graphConfig.widgetConfig?.group_by?.[0],
                    stackByField: graphConfig.widgetConfig?.group_by?.[1],
                    title: graphConfig.title
                }
            };

            onPublish?.(updatedGraph);

            sweetAlert("success", "Graph updated successfully", false);

            onClose(false, updatedGraph);
        } catch (error) {
            console.error(error);
            setError("Failed to update graph");
        } finally {
            setLoading(false);
        }
    }, [editingGraph, getGraphData, onPublish, onClose]);

    const createGraph = useCallback(async (graphConfig) => {
        setLoading(true);
        setError("");
        try {
            const { isSuccess, data, message } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/preview`,
                {},
                graphConfig,
                "POST"
            );

            if (isSuccess && data?.data) {
                const chartData = {
                    data: Array.isArray(data?.data) ? data?.data : [],
                    chartType: graphConfig.chartType,
                    xAxisField: graphConfig.widgetConfig.group_by[0],
                    stackByField: graphConfig.widgetConfig.group_by[1],
                    title: graphConfig.title,
                };

                setGraphs([{ ...graphConfig, id: "preview", chartData }]);

            } else {
                const chartData = {
                    chartType: graphConfig.chartType,
                    xAxisField: graphConfig.widgetConfig.group_by[0],
                    stackByField: graphConfig.widgetConfig.group_by[1],
                    title: graphConfig.title,
                };
                setGraphs([{ ...graphConfig, id: "preview", chartData }]);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch graph data");
        } finally {
            setLoading(false);
        }
    }, [selectedDataSourceType]);

    const handleSubmit = useCallback((graphConfig, action) => {
        if (editingGraph) {
            if (action === "CREATE_PREVIEW") {
                createGraph(graphConfig);
                return;
            }

            if (action === "SAVE_CHANGES") {
                updateGraph(graphConfig);
                return;
            }
        }

        createGraph(graphConfig);
    }, [editingGraph, updateGraph, createGraph, selectedDataSourceType]);

    useEffect(() => {
        if (isOpen) {
            fetchDataSources();
        }
    }, [isOpen, fetchDataSources]);

    useEffect(() => {
        if (isOpen && selectedDataSourceType) {
            fetchFilterOptions(selectedDataSourceType);

            if (editingGraph) {
                const fetchEditingGraphData = async () => {
                    setLoading(true);
                    try {
                        const chartData = {
                            data: Array.isArray(editingGraph.chartData)
                                ? editingGraph.chartData
                                : editingGraph.chartData?.data ?? [],
                            chartType: editingGraph.chartType,
                            xAxisField: editingGraph.widgetConfig?.group_by?.[0],
                            stackByField: editingGraph.widgetConfig?.group_by?.[1],
                            title: editingGraph.title,
                        };
                        const updatedGraph = {
                            ...editingGraph,
                            chartData: chartData
                        };
                        setGraphs([updatedGraph]);
                    } catch (error) {
                        console.error("Error loading graph data:", error);
                        setGraphs([editingGraph]);
                    } finally {
                        setLoading(false);
                    }
                };
                fetchEditingGraphData();
            } else {
                setGraphs([]);
            }
        }
    }, [isOpen, selectedDataSourceType, editingGraph, fetchFilterOptions, getGraphData]);

    const getRepresentationType = (graph) => {
        return graph.representationType || "Graph";
    };

    return (
        <Modal
            show={isOpen}
            onHide={() => {
                if (!isMinimized) {
                    onClose(true);
                    setIsMinimized(false);
                }
            }}
            size={isMinimized ? "sm" : "xl"}
            centered
            backdrop={isMinimized ? false : true}
        >
            <div style={styles.modalHeader}>
                <h3 style={styles.modalHeaderTitle}>
                    📊 {editingGraph ? 'Edit Graph' : 'BI Dashboard'}
                </h3>
                <div style={styles.modalHeaderActions}>
                    {!showCreateSidebar && !isMinimized && (
                        <button
                            style={styles.iconButton}
                            title="Open Sidebar"
                            onClick={() => setShowCreateSidebar(true)}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                        >
                            ☰
                        </button>
                    )}

                    <button
                        style={styles.iconButton}
                        onClick={() => setIsMinimized(!isMinimized)}
                        title={isMinimized ? "Maximize" : "Minimize"}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                    >
                        {isMinimized ? <Maximize2 size={18} color="#6b7280" /> : <Minimize2 size={18} color="#6b7280" />}
                    </button>
                    <button
                        style={styles.iconButton}
                        onClick={() => {
                            onClose(false);
                            setIsMinimized(false);
                        }}
                        title="Close"
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = '#fee2e2';
                            e.currentTarget.style.borderColor = '#fecaca';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                    >
                        <X size={18} color="#6b7280" />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <Modal.Body style={{ padding: 0 }}>
                    <div style={styles.mainContainer}>
                        <div style={{ ...styles.contentArea, width: showCreateSidebar ? '70%' : '100%' }}>
                            <Container style={styles.chatContainer}>
                                <div style={styles.messagesArea}>
                                    {loading && graphs.length === 0 ? (
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner} />
                                            <div style={{ color: '#6b7280', fontSize: '14px' }}>
                                                {editingGraph ? 'Updating graph...' : 'Creating graph...'}
                                            </div>
                                        </div>
                                    ) : error && graphs.length === 0 ? (
                                        <div style={styles.emptyState}>
                                            <div style={styles.emptyStateIcon}>⚠️</div>
                                            <div style={styles.emptyStateTitle}>
                                                {editingGraph ? 'Error Updating Graph' : 'Error Creating Graph'}
                                            </div>
                                            <div style={styles.emptyStateText}>{error}</div>
                                        </div>
                                    ) : graphs.length === 0 ? (
                                        <div style={styles.emptyState}>
                                            <div style={styles.emptyStateIcon}>📊</div>
                                            <div style={styles.emptyStateTitle}>
                                                {editingGraph ? 'Edit Your Graph' : 'No Graph Created'}
                                            </div>
                                            <div style={styles.emptyStateText}>
                                                {editingGraph
                                                    ? 'Update your graph configuration on the right'
                                                    : 'Click "Create" to build your first BI graph'
                                                }
                                            </div>
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col lg={11} xl={10} className="mx-auto">
                                                {graphs.map((graph) => 
                                                    {   
                                                    const representationType = getRepresentationType(graph);
                                                    const isSummary = representationType === "Summary";
                                                    const isTabular = representationType === "Tabular";

                                                    const xAxisFieldLabel = graph.widgetConfig?.group_by?.[0] 
                                                        ? AXIS_FIELD_LABEL_MAP[graph.widgetConfig.group_by[0]] || graph.widgetConfig.group_by[0]
                                                        : null;
                                                    const stackByFieldLabel = graph.widgetConfig?.group_by?.[1]
                                                        ? AXIS_FIELD_LABEL_MAP[graph.widgetConfig.group_by[1]] || graph.widgetConfig.group_by[1]
                                                        : null;

                                                    return <div key={graph.id} style={styles.chartContainer}>
                                                        <div style={styles.chartHeader}>
                                                            <div style={{ flex: 1 }}>
                                                                <h3 style={styles.chartTitle}>{capitalizeWords(graph.title)}</h3>
                                                                {graph.description && (
                                                                    <p style={styles.chartDescription}>
                                                                        {graph.description}
                                                                    </p>
                                                                )}
                                                                <div style={{
                                                                    fontSize: '12px',
                                                                    color: '#9ca3af',
                                                                    marginTop: '6px',
                                                                    display: 'flex',
                                                                    gap: '12px',
                                                                    flexWrap: 'wrap'
                                                                }}>
                                                                    {graph.representationType === 'Graph' && (
                                                                        <>
                                                                            {xAxisFieldLabel && <span>X-Axis: {xAxisFieldLabel}</span>}
                                                                            {stackByFieldLabel && <span>Stacked By: {stackByFieldLabel}</span>}
                                                                        </>
                                                                    )}
                                                                    {graph.widgetConfig?.aggregateSubLocations !== undefined && (
                                                                        <span>Aggregate SubLocations: {graph.widgetConfig.aggregateSubLocations ? 'Yes' : 'No'}</span>
                                                                    )}
                                                                    {graph.representationType && (
                                                                        <span>Representation: {graph.representationType}</span>
                                                                    )}
                                                                    {graph.lastSyncedAt && (
                                                                        <span>
                                                                            Last synced: {new Date(graph.lastSyncedAt).toLocaleString()}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {!graph.chartData && (
                                                            <div style={{ padding: "40px", textAlign: "center" }}>
                                                                <Loader2 className="animate-spin" />
                                                            </div>
                                                        )}

                                                        {graph.chartData && (
                                                            <>
                                                                {isSummary && (
                                                                    <SummaryCard
                                                                        meta={{ ...graph,title: graph.title }}
                                                                        data={graph.chartData?.data}
                                                                        syncing={isPublishing}
                                                                        onEdit={() => onClose(false, graph)}
                                                                        onSync={() => syncGraph(graph.id)}
                                                                        showActionButton={false}
                                                                    />
                                                                )}

                                                                {!isSummary && isTabular && (
                                                                    <TabularView
                                                                        data={graph.chartData?.data}
                                                                        locations={filterOptions.locations}
                                                                        title = {graph.title}
                                                                    />
                                                                )}

                                                                {!isSummary && !isTabular && (
                                                                    <ChartFactory
                                                                        chartData={graph?.chartData}
                                                                        group_by={graph?.widgetConfig?.group_by}
                                                                        chartOptions={{
                                                                            height: 450,
                                                                            stacked: !!(graph?.widgetConfig?.group_by > 1),
                                                                            transform: true
                                                                        }}
                                                                    />
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                })}
                                            </Col>
                                        </Row>
                                    )}
                                </div>

                                {error && graphs.length > 0 && (
                                    <div style={styles.errorContainer}>
                                        <span>⚠️</span>
                                        <span>{error}</span>
                                    </div>
                                )}
                            </Container>
                        </div>

                        <CreateGraphSidebar
                            show={showCreateSidebar}
                            onClose={() => setShowCreateSidebar(false)}
                            onSubmit={handleSubmit}
                            onPublishPreview={publishLatestPreview}
                            isPublishing={isPublishing}
                            filterOptions={filterOptions}
                            editingGraph={editingGraph}
                            dataSources={dataSources}
                            selectedDataSourceType={selectedDataSourceType}
                            onDataSourceChange={handleDataSourceChange}
                        />
                    </div>
                </Modal.Body>
            )}

            <style>{`
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    @keyframes slideDown {
        from { 
            opacity: 0; 
            transform: translateY(-10px); 
            max-height: 0;
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 0;
            padding-bottom: 0;
        }
        to { 
            opacity: 1; 
            transform: translateY(0); 
            max-height: 500px;
            margin-top: 15px;
            margin-bottom: 15px;
            padding-top: 20px;
            padding-bottom: 20px;
        }
    }
`}</style>
        </Modal>
    );
};

export default BiDashboard;